import { Immutable } from "immer";
import React from "react";
import {
    FinancingModalTriggerFinance,
    financingCCs,
} from "tsi-common-react/src/apps/checkout/FinancingModalTriggerFinance/index";
import { FinanceCalculator } from "tsi-common-react/src/apps/financing/FinanceCalculator";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { cmsStyleHook } from "tsi-common-react/src/utils/cmsStyleHook";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FinancingModalTriggerCheckNow } from "../../apps/checkout/FinancingModalTriggerCheckNow";
import { SealyFinanceFAQsBlock } from "../../blocks/SealyFinanceFAQsBlock";
import { SealyFinanceOptionsBlock } from "../../blocks/SealyFinanceOptionsBlock";

import headerStyles from "../../styles/composable/finance-page-header.module.scss";
import styles from "./index.module.scss";

/**
 * Full-page React assembly for `FinancePage` (#33578, Phase 2 FINAL).
 *
 * The legacy `finance_page.html` `{% block layout %}` rendered the
 * `finance_fields` StreamField in THREE scattered `block_type`-filtered
 * `{% for %}` loops interleaved with static chrome and four `include_react`
 * financing widgets. This reproduces the WHOLE layout in one render tree and
 * dispatches each `finance_fields` block in its original section:
 *   - `finance_options` -> table row      (SealyFinanceOptionsBlock)
 *   - `promotion`       -> promotion row  (PromotionBlock)
 *   - `faqs`            -> FAQs row       (SealyFinanceFAQsBlock)
 *
 * The four financing widgets that were separate `include_react` calls are
 * rendered as their React components directly (no nested `include_react`),
 * passing the same props the original `include_react` calls did:
 *   - FinancingCheckNow -> FinancingModalTriggerCheckNow (sealy-local; the
 *     common `FinancingCheckNowTemplate` is sealy's own re-implementation, so
 *     this IS the widget the legacy include rendered). `classString` == legacy
 *     `extra_classes`. NOTE the sealy legacy hero placed this include DIRECTLY
 *     inside `.finance-hero` (no `.financing-modal-trigger` wrapper -- contrast
 *     stearns).
 *   - FinancingForm -> FinancingModalTrigger modalType="full-app"
 *     (mirrors `FinancingFormTemplate`): className/title/applicationSource +
 *     children == legacy link_class/link_title/application_source/link_text.
 *   - FinanceCalculator -> FinanceCalculator (mirrors `FinanceCalculatorTemplate`).
 *   - FinancingTriggerFinance -> FinancingModalTriggerFinance ccSource=sealy
 *     (mirrors `FinancingTriggerFinanceTemplate`): cc_source="sealy",
 *     application_source="Financing Page".
 *
 * Sealy-vs-stearns layout divergences reproduced from the SEALY legacy
 * template (the source of truth; stearns' assembly is only the skeleton):
 *   - hero check-now trigger is NOT wrapped in `.financing-modal-trigger`;
 *   - benefits list item 4 is the Sealy credit-card copy;
 *   - the promotion loop renders inside its own
 *     `.finance-page-row--usage-and-help` row in a `.finance-promotion` div
 *     (no per-block wrapper div);
 *   - the terms row has no stearns-only stray `tet` class.
 *
 * Page-level data the client side does not otherwise have (default plan terms,
 * `|richtext`-expanded headline, the pre-approval disclosure HTML, the hero
 * image URL) is threaded by the `get_finance_page_body` helper.
 */

const APPLICATION_SOURCE = "Financing Page";

type IProps = Immutable<
    Omit<templates.SealyFinancePageTemplate, "page_chrome" | "is_preview">
>;

type FinanceField = IProps["page"]["finance_fields"]["value"][number];
type FinanceOptionsField = Extract<FinanceField, { type: "finance_options" }>;
type PromotionField = Extract<FinanceField, { type: "promotion" }>;
type FAQsField = Extract<FinanceField, { type: "faqs" }>;

export const SealyFinancePageBody = (props: IProps): React.JSX.Element => {
    const applyNowText = t`Apply Now`;
    const fields = props.page.finance_fields.value;

    // Filter the single StreamField value into the three sections, mirroring the
    // three legacy `{% for block in self.finance_fields %}` / `{% if
    // block.block_type == ... %}` loops.
    const financeOptions = fields.filter(
        (b): b is FinanceOptionsField => b.type === "finance_options",
    );
    const promotions = fields.filter(
        (b): b is PromotionField => b.type === "promotion",
    );
    const faqs = fields.filter((b): b is FAQsField => b.type === "faqs");

    // Interpolated link for the Wells Fargo `jt` sentence below.
    const wellsFargoLink = (
        <a key="wf" href="https://wellsfargo.com/cardholders">
            wellsfargo.com/cardholders
        </a>
    );

    return (
        <>
            <div
                className={concatClassNames([
                    styles.financeHero,
                    "hero",
                    "l-full-width",
                    "u-margin-bottom--xl",
                ])}
                style={
                    props.hero_image_url
                        ? {
                              backgroundImage: `url(${props.hero_image_url})`,
                          }
                        : undefined
                }
            >
                <div
                    className={concatClassNames([
                        styles.financeHeroContent,
                        "l-capped-width",
                    ])}
                >
                    <div
                        className={styles.financeHeroCopy}
                        dangerouslySetInnerHTML={{
                            __html: props.headline_html,
                        }}
                    />
                </div>
                <FinancingModalTriggerCheckNow
                    classString={concatClassNames([
                        styles.financeHeroButtonPreQual,
                        "button",
                        "al-financing-page__hero--check-now",
                    ])}
                />
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
            >
                <div
                    className={concatClassNames([
                        cmsStyleHook("finance-page-offer"),
                        styles.financePageOffer,
                    ])}
                >
                    <div className={styles.financePageOfferInnerContainer}>
                        <h1
                            className={concatClassNames([
                                cmsStyleHook("finance-page-offer__terms"),
                                styles.financePageOfferTerms,
                            ])}
                        >
                            <span
                                className={concatClassNames([
                                    styles.financePageOfferTermsLine,
                                    styles.financePageOfferTermsLine1,
                                ])}
                            >
                                {props.apr}% APR
                            </span>
                            <span
                                className={concatClassNames([
                                    styles.financePageOfferTermsLine,
                                    styles.financePageOfferTermsLine2,
                                ])}
                            >
                                {t`for`}{" "}
                                <span
                                    className={styles.financePageOfferLineRule}
                                />
                            </span>
                            <span
                                className={concatClassNames([
                                    styles.financePageOfferTermsLine,
                                    styles.financePageOfferTermsLine3,
                                ])}
                            >
                                {props.term_months} {t`Months`}
                                <sup>
                                    <a href="#terms_and_conditions">
                                        {props.fine_print_superscript}
                                        <span className="ada-screenreader-only">
                                            {t`Additional information about Terms and Conditions`}
                                        </span>
                                    </a>
                                </sup>
                            </span>
                        </h1>
                        <div className={styles.financePageOfferButtons}>
                            <FinancingModalTrigger
                                modalType="full-app"
                                className={concatClassNames([
                                    styles.financePageOfferButton,
                                    styles.financePageOfferButtonApply,
                                    "button",
                                ])}
                                title={applyNowText}
                                applicationSource={APPLICATION_SOURCE}
                            >
                                {applyNowText}
                            </FinancingModalTrigger>
                            <a
                                className={concatClassNames([
                                    styles.financePageOfferButton,
                                    styles.financePageOfferButtonTerms,
                                ])}
                                href="#terms_and_conditions"
                            >
                                {t`Financial Terms`}
                                <span className="ada-screenreader-only">
                                    {t`Additional information about Terms and Conditions`}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
                <div className={styles.financePageBenefits}>
                    <ul className={styles.financePageBenefitsList}>
                        <li className={styles.financePageBenefitsListItem}>
                            {t`No annual fee`}
                            <sup>
                                <a href="#terms_and_conditions">
                                    1
                                    <span className="ada-screenreader-only">
                                        {t`Additional information about Terms and Conditions`}
                                    </span>
                                </a>
                            </sup>
                        </li>
                        <li className={styles.financePageBenefitsListItem}>
                            {t`Quick and secure online application`}
                        </li>
                        <li className={styles.financePageBenefitsListItem}>
                            {t`Apply now, or in checkout`}
                        </li>
                        <li className={styles.financePageBenefitsListItem}>
                            {t`The Sealy Credit Card cannot be used for purchases at any other merchants`}
                        </li>
                    </ul>
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
            >
                <div
                    className={concatClassNames([
                        cmsStyleHook("finance-table"),
                        styles.financeTable,
                    ])}
                >
                    {financeOptions.map((block) => (
                        <SealyFinanceOptionsBlock
                            key={block.id}
                            value={block.value}
                        />
                    ))}
                </div>
                <div className="finance-calculator-container">
                    <FinanceCalculator />
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    styles.financePageRowUsageAndHelp,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
            >
                <div className={styles.financeHelp}>
                    <h5 className={styles.financeHelpTitle}>
                        {t`Need help with your existing Wells Fargo Account?`}
                    </h5>
                    <div className={styles.financeHelpContent}>
                        <a
                            href="https://retailservices.wellsfargo.com/customer/index.html?returnFull=1"
                            target="_blank"
                            rel="noreferrer"
                            className={concatClassNames([
                                styles.financeHelpButton,
                                "button",
                                "button--inverse",
                            ])}
                        >
                            {t`Access your Account`}
                        </a>
                        <span>
                            {/* Single `{% blocktrans %}` with an embedded link in
                                the legacy template -> kept as one translatable
                                sentence via `jt` (codebase convention). */}
                            {jt`Contact Wells Fargo Bank, N.A. at 877-805-7744 or sign on at ${wellsFargoLink} to manage your account.`}
                            <a
                                href="https://wellsfargo.com/cardholders"
                                className={concatClassNames([
                                    styles.financeHelpLink,
                                    "al-financing-page__acccess-your-account",
                                ])}
                            >
                                {t`Access your Account`}
                            </a>
                        </span>
                    </div>
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    styles.financePageRowUsageAndHelp,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
            >
                <div className="finance-promotion">
                    {promotions.map((block) => (
                        // GA4 parity: NO `blockIndex` (defaults to 1). The
                        // legacy loop rendered each promotion via
                        // `{% include_block block %}` -> promotion-block.html
                        // -> `{% include_react PromotionBlockTemplate
                        // value=value %}`, which passes no index, so EVERY
                        // promotion on this page emitted
                        // `creative_slot: "promotion-block-1"` /
                        // `promotion_id: "<base>-1"` regardless of position.
                        // Do not copy the stearns-assembly `idx + 1` here.
                        <PromotionBlock key={block.id} value={block.value} />
                    ))}
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.financePagePreApprovalAdMobile,
                    "pre-approval-ad",
                    "pre-approval-ad--finance-page",
                    "u-flex-container",
                ])}
            >
                <div className="pre-approval-ad__or-and-see">
                    <div className="pre-approval-ad__see">
                        <div className="pre-approval-ad__see--bold">
                            {t`Check if you're pre-qualified`}
                        </div>
                        <div className="pre-approval-ad__see--small">
                            {t`without affecting your credit score.`}
                        </div>
                    </div>
                </div>
                <FinancingModalTriggerCheckNow classString="button pre-approval-ad__button pre-approval-ad__button--mobile pre-approval-ad__check" />
                <div className="pre-approval-ad__apply-alternate">
                    {t`or`}{" "}
                    <FinancingModalTrigger
                        modalType="full-app"
                        className="pre-approval-ad__apply-alternate-link"
                        title={applyNowText}
                        applicationSource={APPLICATION_SOURCE}
                    >
                        {applyNowText}
                    </FinancingModalTrigger>
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    styles.financePageRowFaqs,
                    "l-capped-width",
                    "u-margin-bottom--xl",
                ])}
            >
                <h3 className={headerStyles.financePageHeader}>
                    {t`Frequently Asked Questions`}
                </h3>
                {faqs.map((block) => (
                    <div key={block.id} className="finance-faqs__faqs">
                        <SealyFinanceFAQsBlock value={block.value} />
                    </div>
                ))}
            </div>

            <div
                className={concatClassNames([
                    styles.financePageRow,
                    styles.financePageTerms,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
                id="terms_and_conditions"
                dangerouslySetInnerHTML={{ __html: props.terms_html }}
            />

            <div
                className={concatClassNames([
                    styles.financePagePreApprovalAds,
                    "l-capped-width",
                    "u-margin-bottom--l",
                ])}
            >
                <FinancingModalTriggerFinance
                    ccSource={financingCCs.SEALY}
                    applicationSource={APPLICATION_SOURCE}
                />
            </div>
        </>
    );
};
